/**
 * GovDashboard — Painel institucional (KPIs consolidados).
 *
 * Sprint 2 · v0.98 · Fev/2026.
 * Consome GET /api/gov/dashboard/kpis.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HUMAN_LABEL_BY_HOUR = (h) => {
  const p = (n) => n.toString().padStart(2, "0");
  return `${p(h)}h`;
};

export default function GovDashboard() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/gov/dashboard/kpis");
        setKpis(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao carregar painel");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="text-slate-500 text-sm" data-testid="gov-dashboard-loading">
        Carregando painel institucional…
      </div>
    );
  }
  if (!kpis) return null;

  const hourMax = Math.max(...(kpis.hourly_activity_7d || [1]), 1);

  return (
    <div className="space-y-8" data-testid="gov-dashboard-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-700 font-semibold">
          Painel Institucional
        </div>
        <h1 className="font-heading text-[26px] font-semibold text-slate-900 mt-1">
          {kpis.tenant?.name || "Órgão Público"}
        </h1>
        <p className="text-[13px] text-slate-500 mt-1">
          Indicadores consolidados de atendimento ao cidadão e da Base de
          Conhecimento Institucional.
        </p>
      </header>

      {/* KPI cards principais */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Cidadãos atendidos"
          value={kpis.citizens?.total ?? 0}
          hint={`+${kpis.citizens?.new_30d ?? 0} nos últimos 30 dias`}
          testid="gov-kpi-citizens"
        />
        <KpiCard
          label="Conversas (30 dias)"
          value={kpis.conversations?.last_30d ?? 0}
          hint={`Hoje: ${kpis.conversations?.today ?? 0} · 7d: ${kpis.conversations?.last_7d ?? 0}`}
          testid="gov-kpi-conversations"
        />
        <KpiCard
          label="Documentos oficiais"
          value={kpis.knowledge_base?.total_documents ?? 0}
          hint={`${kpis.knowledge_base?.total_chunks ?? 0} fragmentos indexados`}
          testid="gov-kpi-documents"
        />
        <KpiCard
          label="Resolução automática"
          value={`${kpis.auto_resolution_pct_30d ?? 0}%`}
          hint="Últimos 30 dias"
          testid="gov-kpi-auto-resolution"
        />
      </section>

      {/* Economia + créditos */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard
          label="Economia estimada"
          value={new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })
            .format(kpis.human_savings_30d?.money_saved_brl || 0)}
          hint={`${kpis.human_savings_30d?.minutes_saved || 0} min de atendimento humano`}
          accent="emerald"
          testid="gov-kpi-savings"
        />
        <KpiCard
          label="Atendimentos pela IA"
          value={kpis.human_savings_30d?.attendances_by_ai ?? 0}
          hint="Últimos 30 dias"
          testid="gov-kpi-ai-handled"
        />
        <KpiCard
          label="Créditos IA (30d)"
          value={(kpis.credits_consumed_30d ?? 0).toLocaleString("pt-BR")}
          hint={`${(kpis.messages_30d?.total ?? 0).toLocaleString("pt-BR")} mensagens processadas`}
          testid="gov-kpi-credits"
        />
      </section>

      {/* Atendimentos por Secretaria */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-heading text-[15px] font-semibold text-slate-900">
          Base de Conhecimento por Secretaria
        </h2>
        <p className="text-[11.5px] text-slate-500 mt-0.5">
          Documentos oficiais organizados por área do órgão.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {(kpis.knowledge_base?.by_secretaria || []).map((s) => (
            <div
              key={s.code}
              data-testid={`gov-dashboard-sec-${s.code}`}
              className={`px-4 py-3 rounded-lg border ${s.documents > 0 ? "bg-emerald-50/50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
            >
              <div className="text-[12px] font-medium text-slate-900">{s.label}</div>
              <div className="text-[10.5px] text-slate-500 mt-1">
                {s.documents} {s.documents === 1 ? "documento" : "documentos"}
                {" · "}
                {s.chunks} {s.chunks === 1 ? "fragmento" : "fragmentos"}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Top tópicos + Top docs */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-heading text-[14px] font-semibold text-slate-900">
            Temas mais pesquisados (30d)
          </h3>
          {(kpis.top_topics_30d || []).length === 0 ? (
            <p className="mt-3 text-[12px] text-slate-500">
              Nenhuma busca institucional registrada ainda.
            </p>
          ) : (
            <ul className="mt-3 space-y-1.5" data-testid="gov-top-topics">
              {kpis.top_topics_30d.slice(0, 8).map((t, i) => (
                <li key={t.secretaria || `unfiled-${i}`} className="flex items-center justify-between text-[12.5px]">
                  <span className="text-slate-700">{t.label}</span>
                  <span className="text-slate-400 font-medium">{t.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-heading text-[14px] font-semibold text-slate-900">
            Documentos mais utilizados
          </h3>
          {(kpis.top_documents || []).length === 0 ? (
            <p className="mt-3 text-[12px] text-slate-500">
              Ainda não há documentos indexados na Base Institucional.
            </p>
          ) : (
            <ul className="mt-3 space-y-1.5" data-testid="gov-top-docs">
              {kpis.top_documents.slice(0, 5).map((d) => (
                <li key={d.id} className="text-[12.5px] flex items-center justify-between gap-2">
                  <span className="text-slate-700 truncate">{d.filename}</span>
                  <span className="text-slate-400 font-medium whitespace-nowrap">
                    {d.chunks} frag.
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Histograma de horários */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-heading text-[14px] font-semibold text-slate-900">
          Horários de maior utilização (7 dias)
        </h3>
        <div
          className="mt-4 flex gap-1 items-end h-32"
          data-testid="gov-hourly-histogram"
        >
          {(kpis.hourly_activity_7d || []).map((v, h) => {
            const pct = hourMax > 0 ? Math.round((v / hourMax) * 100) : 0;
            return (
              <div key={h} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <div
                  className="w-full rounded-t bg-emerald-500/80"
                  style={{ height: `${Math.max(pct, v > 0 ? 6 : 2)}%` }}
                  title={`${HUMAN_LABEL_BY_HOUR(h)} — ${v} mensagens`}
                />
                <div className="text-[9px] text-slate-400">{h}</div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function KpiCard({ label, value, hint, accent, testid }) {
  const bg = accent === "emerald" ? "bg-emerald-700" : "bg-slate-900";
  const txt = accent === "emerald" ? "text-white" : "text-white";
  return (
    <div className={`rounded-xl p-4 ${bg} ${txt}`} data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-white/70">
        {label}
      </div>
      <div className="mt-2 text-2xl font-heading font-semibold">{value}</div>
      {hint ? (
        <div className="mt-1 text-[10.5px] text-white/70">{hint}</div>
      ) : null}
    </div>
  );
}
